import { useState } from "react";
import { KeyRound, MoreVertical, Search, Trash2, UserPlus, Users } from "lucide-react";
import { Card } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Button } from "@/shared/ui/button.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Label } from "@/shared/ui/label.jsx";
import { Switch } from "@/shared/ui/switch.jsx";
import { Spinner, EmptyState } from "@/shared/ui/spinner.jsx";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/shared/ui/table.jsx";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/dialog.jsx";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/ui/alert-dialog.jsx";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu.jsx";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/ui/select.jsx";
import { initials, formatDateTime } from "@/shared/lib/format.js";
import { useDebounced } from "@/shared/hooks/use-debounced.js";
import { useAuth } from "@/features/auth/auth-context.jsx";
import { CredentialsDialog } from "./components/CredentialsDialog.jsx";
import {
  useCreateUser, useDeleteUser, useResetUserPassword, useUpdateUser, useUsers,
} from "./use-users.js";

/**
 * Gestão de usuários.
 *
 * UM componente serve as duas telas — `/usuarios` (equipe da clínica) e
 * `/master/usuarios` (equipe da plataforma). Muda só o `escopo` e a lista
 * de perfis. No app antigo eram dois arquivos com o mesmo layout, uma
 * lógica de senha em texto puro de um lado e Supabase Auth do outro.
 *
 * @param {{ escopo: "clinic" | "master", roleOptions: {value: string, label: string}[] }} props
 */
export function UsersManager({ escopo, roleOptions }) {
  const { user: usuarioAtual } = useAuth();

  const [busca, setBusca] = useState("");
  const [perfil, setPerfil] = useState("todos");
  const buscaAdiada = useDebounced(busca, 300);

  const { data: usuarios = [], isLoading } = useUsers({
    escopo,
    search: buscaAdiada,
    role: perfil,
  });

  const criar = useCreateUser(escopo);
  const atualizar = useUpdateUser(escopo);
  const redefinir = useResetUserPassword(escopo);
  const excluir = useDeleteUser(escopo);

  const [novoAberto, setNovoAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState(null);
  const [paraExcluir, setParaExcluir] = useState(null);
  const [credenciais, setCredenciais] = useState(null);

  /** Guarda a senha devolvida quando o e-mail falha, para exibir no diálogo. */
  const capturarCredenciais = (resultado) => {
    if (resultado?.password) setCredenciais(resultado);
  };

  return (
    <>
      <div className="space-y-4 p-6">
        {/* ----------------------------------------------------- filtros */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-60 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por nome ou e-mail..."
                className="pl-9"
                aria-label="Buscar usuários"
              />
            </div>

            <Select value={perfil} onValueChange={setPerfil}>
              <SelectTrigger className="w-52">
                <SelectValue placeholder="Perfil" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os perfis</SelectItem>
                {roleOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button size="sm" className="gap-1.5" onClick={() => setNovoAberto(true)}>
              <UserPlus className="h-4 w-4" />
              Novo usuário
            </Button>
          </div>
        </Card>

        {/* ------------------------------------------------------ tabela */}
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-t-0 hover:bg-transparent">
                <TableHead>Usuário</TableHead>
                <TableHead>Perfil</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead>Último acesso</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center">
                    <Spinner className="mx-auto h-6 w-6 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : usuarios.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <EmptyState
                      icon={Users}
                      title="Nenhum usuário encontrado"
                      description="Ajuste a busca ou cadastre uma nova pessoa."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                usuarios.map((usuario) => {
                  const ehVoce = usuario.id === usuarioAtual?.id;
                  return (
                    <TableRow key={usuario.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-chart-2/20 text-xs font-semibold">
                            {initials(usuario.fullName)}
                          </div>
                          <div className="min-w-0">
                            <p className="flex items-center gap-2 font-medium leading-tight">
                              {usuario.fullName}
                              {ehVoce && (
                                <Badge variant="outline" className="text-[10px]">
                                  você
                                </Badge>
                              )}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">{usuario.email}</p>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <Badge variant="secondary">{usuario.roleLabel}</Badge>
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={usuario.active}
                            disabled={ehVoce || atualizar.isPending}
                            onCheckedChange={(valor) =>
                              atualizar.mutate({ id: usuario.id, active: valor })
                            }
                            aria-label={`${usuario.active ? "Desativar" : "Ativar"} ${usuario.fullName}`}
                          />
                          <span className="text-xs text-muted-foreground">
                            {usuario.active ? "Ativo" : "Inativo"}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell className="text-xs text-muted-foreground">
                        {usuario.lastAccessAt ? formatDateTime(usuario.lastAccessAt) : "Nunca acessou"}
                      </TableCell>

                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Ações">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEmEdicao(usuario)}>
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                redefinir.mutate(
                                  { id: usuario.id },
                                  { onSuccess: capturarCredenciais },
                                )
                              }
                            >
                              <KeyRound className="h-4 w-4" />
                              Redefinir senha
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              variant="destructive"
                              disabled={ehVoce}
                              onClick={() => setParaExcluir(usuario)}
                            >
                              <Trash2 className="h-4 w-4" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* -------------------------------------------------------- diálogos */}
      <UserFormDialog
        aberto={novoAberto}
        onClose={() => setNovoAberto(false)}
        roleOptions={roleOptions}
        mutation={criar}
        onSuccess={capturarCredenciais}
      />

      <UserFormDialog
        aberto={Boolean(emEdicao)}
        usuario={emEdicao}
        onClose={() => setEmEdicao(null)}
        roleOptions={roleOptions}
        mutation={atualizar}
      />

      <AlertDialog open={Boolean(paraExcluir)} onOpenChange={(e) => !e && setParaExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{paraExcluir?.fullName}</strong> perderá o acesso imediatamente. Esta ação
              não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setParaExcluir(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={excluir.isPending}
              onClick={(evento) => {
                evento.preventDefault();
                excluir.mutate(paraExcluir.id, { onSuccess: () => setParaExcluir(null) });
              }}
            >
              {excluir.isPending ? <Spinner /> : null}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CredentialsDialog resultado={credenciais} onClose={() => setCredenciais(null)} />
    </>
  );
}

/**
 * Formulário de criar/editar — o mesmo em ambos os casos, diferindo apenas
 * pela presença de `usuario`.
 */
function UserFormDialog({ aberto, usuario, onClose, roleOptions, mutation, onSuccess }) {
  return (
    <Dialog open={aberto} onOpenChange={(estado) => !estado && onClose()}>
      <DialogContent>
        {aberto && (
          <FormularioUsuario
            key={usuario?.id ?? "novo"}
            usuario={usuario}
            roleOptions={roleOptions}
            mutation={mutation}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormularioUsuario({ usuario, roleOptions, mutation, onClose, onSuccess }) {
  const editando = Boolean(usuario);

  const [form, setForm] = useState(() => ({
    fullName: usuario?.fullName ?? "",
    email: usuario?.email ?? "",
    role: usuario?.role ?? roleOptions[0].value,
  }));

  const set = (campo) => (valor) => setForm((atual) => ({ ...atual, [campo]: valor }));

  function submeter(evento) {
    evento.preventDefault();
    mutation.mutate(editando ? { id: usuario.id, ...form } : form, {
      onSuccess: (resultado) => {
        onSuccess?.(resultado);
        onClose();
      },
    });
  }

  return (
    <form onSubmit={submeter}>
      <DialogHeader>
        <DialogTitle>{editando ? "Editar usuário" : "Novo usuário"}</DialogTitle>
        <DialogDescription>
          {editando
            ? "Altere os dados de acesso desta pessoa."
            : "A senha é gerada automaticamente e enviada por e-mail."}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="user-name">Nome completo</Label>
          <Input
            id="user-name"
            required
            maxLength={120}
            value={form.fullName}
            onChange={(e) => set("fullName")(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="user-email">E-mail</Label>
          <Input
            id="user-email"
            type="email"
            required
            maxLength={160}
            value={form.email}
            onChange={(e) => set("email")(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>Perfil de acesso</Label>
          <Select value={form.role} onValueChange={set("role")}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? <Spinner /> : null}
          {editando ? "Salvar" : "Criar usuário"}
        </Button>
      </DialogFooter>
    </form>
  );
}
